import { useCallback, useMemo } from "react";
import type { Column, Table as TanstackTable } from "@tanstack/react-table";

/**
 * Hook to analyze the columns in the table to determine the visual position and pinning status
 * @param table - The table to analyze
 */
export const useColumnOrder = <T>({ table }: { table: TanstackTable<T> }) => {
  const getColumnIndexes = useCallback((column: Column<T>) => {
    if (column.columns?.length > 0) {
      return column.columns.map((c) => c.getIndex());
    }
    return [column.getIndex()];
  }, []);

  const columnOrder = useMemo(() => {
    const left: number[] = [];
    const right: number[] = [];
    const middle: number[] = [];

    table.getAllColumns().forEach((column) => {
      if (column.getIsPinned() === "left") {
        left.push(...getColumnIndexes(column));
      } else if (column.getIsPinned() === "right") {
        right.push(...getColumnIndexes(column));
      } else {
        middle.push(...getColumnIndexes(column));
      }
    });
    return left.concat(middle).concat(right);
  }, [table, getColumnIndexes]);

  return {
    columnOrder,
  };
};
